/* global SideDrawer */

class CartDrawer extends SideDrawer {
  /**
   * @param {number} cents - Price in cents.
   * @param {string} format - Shopify money format, e.g. "€{{amount}}".
   * @returns {string}
   */
  static formatMoney(cents, format) {
    let value = cents;
    if (typeof value === 'string') value = parseInt(value.replace('.', ''), 10);
    if (Number.isNaN(value)) return '';
    const moneyFormat = format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatMatch = moneyFormat.match(placeholderRegex);
    const placeholder = formatMatch ? formatMatch[1] : 'amount';

    function defaultTo(v, d) {
      return v == null || v !== v ? d : v;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      const p = defaultTo(precision, 2);
      const t = defaultTo(thousands, ',');
      const dec = defaultTo(decimal, '.');
      if (Number.isNaN(number) || number == null) return '0';
      const n = (number / 100.0).toFixed(p);
      const parts = n.split('.');
      const intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${t}`);
      return intPart + (parts[1] ? dec + parts[1] : '');
    }

    let formatted;
    switch (placeholder) {
      case 'amount':
        formatted = formatWithDelimiters(value, 2);
        break;
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 2, "'", '.');
        break;
      default:
        formatted = formatWithDelimiters(value, 2);
    }
    return moneyFormat.replace(placeholderRegex, formatted);
  }

  static variantMatchesSelections(variant, selections) {
    const keys = ['option1', 'option2', 'option3'];
    for (let i = 0; i < selections.length; i += 1) {
      const sel = selections[i];
      if (sel == null || sel === '') return false;
      if (variant[keys[i]] !== sel) return false;
    }
    return true;
  }

  /** Longest contiguous match from option1 upward (used when combo is invalid). */
  static variantPrefixMatchLength(variant, selections) {
    const keys = ['option1', 'option2', 'option3'];
    let len = 0;
    for (let i = 0; i < selections.length; i += 1) {
      const sel = selections[i];
      if (sel == null || sel === '') break;
      if (variant[keys[i]] === sel) len += 1;
      else break;
    }
    return len;
  }

  /**
   * @param {object[]} variants
   * @param {string[]} selections - One entry per option position.
   * @returns {object|undefined}
   */
  static resolvePairingVariant(variants, selections) {
    const exact = variants.find((v) => CartDrawer.variantMatchesSelections(v, selections));
    if (exact) return exact;

    let bestLen = -1;
    let best = [];
    variants.forEach((v) => {
      const len = CartDrawer.variantPrefixMatchLength(v, selections);
      if (len > bestLen) {
        bestLen = len;
        best = [v];
      } else if (len === bestLen) best.push(v);
    });
    return best.find((v) => v.available) || best[0] || variants.find((v) => v.available) || variants[0];
  }

  static isSizeOptionLabel(labelText) {
    const normalized = (labelText || '').replace(/:\s*$/, '').trim().toLowerCase();
    return /size|taille|grosse|groesse|größe|μέγε|μεγε/.test(normalized);
  }

  static findSizeSelectInContainer(container) {
    if (!container) return null;
    const rows = container.querySelectorAll('.cart-drawer-pairing-line__option-row');
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const label = row.querySelector('.cart-item__variant-label');
      const select = row.querySelector('.cart-drawer-pairing-line__option-select');
      if (label && select && CartDrawer.isSizeOptionLabel(label.textContent)) {
        return select;
      }
    }
    return null;
  }

  static readSizeValueFromContainer(container) {
    if (!container) return '';
    const sizeSelect = CartDrawer.findSizeSelectInContainer(container);
    if (sizeSelect && sizeSelect.value) return sizeSelect.value.trim();

    const rows = container.querySelectorAll('.cart-drawer-pairing-line__option-row');
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const label = row.querySelector('.cart-item__variant-label');
      if (!label || !CartDrawer.isSizeOptionLabel(label.textContent)) continue;
      const toggleValue = row.querySelector('.cart-drawer-variant-dd__value');
      if (toggleValue && toggleValue.textContent) return toggleValue.textContent.trim();
    }

    return '';
  }

  static readMainLineSizeValue(cartItem) {
    if (!cartItem) return '';

    const shell = cartItem.querySelector('.cart-drawer-main-line__options-shell');
    if (shell) {
      const optionsInner = shell.querySelector('.cart-drawer-pairing-line__options');
      const sizeValue = CartDrawer.readSizeValueFromContainer(optionsInner);
      if (sizeValue) return sizeValue;
    }

    const variantRows = cartItem.querySelectorAll('.cart-item__description .cart-item__variant');
    for (let i = 0; i < variantRows.length; i += 1) {
      const row = variantRows[i];
      const label = row.querySelector('.cart-item__variant-label');
      const value = row.querySelector('.cart-item__variant-value');
      if (label && value && CartDrawer.isSizeOptionLabel(label.textContent)) {
        return value.textContent.trim();
      }
    }

    return '';
  }

  static variantSizeValue(variant, sizeOptionIndex) {
    if (!variant || sizeOptionIndex < 0) return '';
    const key = ['option1', 'option2', 'option3'][sizeOptionIndex];
    return key && variant[key] ? String(variant[key]).trim() : '';
  }

  static resolveVariantForSize(variants, targetSize, sizeOptionIndex) {
    if (!variants || !variants.length) return null;
    const normalizedTarget = (targetSize || '').trim().toLowerCase();
    if (normalizedTarget && sizeOptionIndex >= 0) {
      const match = variants.find((variant) => {
        if (!variant.available) return false;
        return CartDrawer.variantSizeValue(variant, sizeOptionIndex).toLowerCase() === normalizedTarget;
      });
      if (match) return match;
    }
    return variants.find((v) => v.available) || variants[0];
  }

  static syncCompleteTheSetLineToSize(line, targetSize) {
    const wrapper = line.closest('.cart-drawer-pairing-line__wrapper, .cart-drawer-complete-the-set-line__wrapper');
    if (!wrapper) return;

    const jsonEl = wrapper.querySelector('.cart-drawer-pairing-variants-json');
    if (!jsonEl) return;

    let variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }
    if (!variants || !variants.length) return;

    const sizeOptionIndex = parseInt(wrapper.dataset.sizeOptionIndex, 10);
    const variant = CartDrawer.resolveVariantForSize(
      variants,
      targetSize,
      Number.isNaN(sizeOptionIndex) ? -1 : sizeOptionIndex
    );
    if (!variant) return;

    CartDrawer.syncOptionSelectsToVariant(line, variant);
    CartDrawer.syncPairingLineFromOptions(line);
  }

  static syncCompleteTheSetFromMainLine(cartItem, targetSize) {
    if (!cartItem) return;
    const setRoot = cartItem.querySelector('.cart-item__complete-the-set');
    if (!setRoot) return;

    const sizeValue = typeof targetSize === 'string' ? targetSize : CartDrawer.readMainLineSizeValue(cartItem);
    if (!sizeValue) return;

    setRoot.querySelectorAll('.cart-drawer-complete-the-set-line, .js-cart-page-pairing-line').forEach((line) => {
      CartDrawer.syncCompleteTheSetLineToSize(line, sizeValue);
    });
  }

  static syncAllCompleteTheSetLines(container) {
    if (!container || !container.querySelectorAll) return;
    container.querySelectorAll('.cart-item').forEach((cartItem) => {
      if (cartItem.closest('.merge-remove-item')) return;
      CartDrawer.syncCompleteTheSetFromMainLine(cartItem);
    });
  }

  static getCartProductIdsFromDom(container) {
    const ids = new Set();
    if (!container || !container.querySelectorAll) return ids;
    container.querySelectorAll('[data-merge-list-item] > .cart-item[data-product-id]').forEach((el) => {
      if (el.closest('.merge-remove-item, .merge-remove-wrapper')) return;
      const id = el.dataset.productId;
      if (id) ids.add(String(id));
    });
    return ids;
  }

  static hideCompleteTheSetProductsInCart(container, extraProductIds) {
    if (!container || !container.querySelectorAll) return;

    const ids = CartDrawer.getCartProductIdsFromDom(container);
    const extras = extraProductIds == null ? [] : (Array.isArray(extraProductIds) ? extraProductIds : [extraProductIds]);
    extras.forEach((id) => {
      if (id != null && id !== '') ids.add(String(id));
    });
    if (!ids.size) return;

    container.querySelectorAll('.cart-drawer-complete-the-set-line__wrapper').forEach((wrapper) => {
      if (wrapper.closest('.merge-remove-item')) return;
      const productEl = wrapper.dataset.productId ? wrapper : wrapper.querySelector('[data-product-id]');
      const productId = productEl && productEl.dataset.productId;
      if (productId && ids.has(String(productId))) {
        wrapper.remove();
      }
    });

    container.querySelectorAll('.cart-drawer-complete-the-set-root').forEach((root) => {
      if (!root.querySelector('.cart-drawer-complete-the-set-line__wrapper')) {
        root.classList.add('hidden');
      } else {
        root.classList.remove('hidden');
      }
    });
  }

  static syncCompleteTheSetBlocksFromHtml(newContent, targetContainer) {
    if (!newContent || !targetContainer || !newContent.querySelectorAll || !targetContainer.querySelectorAll) return;

    const liveBlocks = Array.from(targetContainer.querySelectorAll('.cart-item__complete-the-set[data-complete-the-set-line]'));
    newContent.querySelectorAll('.cart-item__complete-the-set[data-complete-the-set-line]').forEach((newEl) => {
      const lineKey = newEl.dataset.completeTheSetLine;
      if (!lineKey) return;
      const targetEl = liveBlocks.find((el) => el.dataset.completeTheSetLine === lineKey);
      if (!targetEl || targetEl.closest('.merge-remove-item')) return;
      targetEl.innerHTML = newEl.innerHTML;
    });

    if (typeof window.initCartDrawerVariantDropdowns === 'function') {
      window.initCartDrawerVariantDropdowns(targetContainer);
    }
    if (window.theme && typeof window.theme.manuallyLoadImages === 'function') {
      window.theme.manuallyLoadImages(targetContainer);
    }
    CartDrawer.syncAllCompleteTheSetLines(targetContainer);
  }

  static syncOptionSelectsToVariant(line, variant) {
    const selects = CartDrawer.getPairingOptionSelects(line);
    const keys = ['option1', 'option2', 'option3'];
    selects.forEach((sel) => {
      const idx = parseInt(sel.dataset.optionIndex, 10);
      if (Number.isNaN(idx) || idx < 0) return;
      const nextVal = variant[keys[idx]];
      if (nextVal != null && sel.value !== nextVal) {
        const opt = Array.from(sel.options).find((o) => o.value === nextVal);
        if (opt) sel.value = nextVal;
      }
    });
    if (window.theme && typeof window.theme.syncCartDrawerOptionDropdownUI === 'function') {
      selects.forEach((s) => theme.syncCartDrawerOptionDropdownUI(s));
    }
  }

  static getPairingOptionSelects(line) {
    return Array.from(line.querySelectorAll('.cart-drawer-pairing-line__option-select')).sort(
      (a, b) => parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10)
    );
  }

  static readPairingSelections(line) {
    const nodes = Array.from(
      line.querySelectorAll('.cart-drawer-pairing-line__option-select, .cart-drawer-pairing-line__option-fixed')
    ).sort((a, b) => parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10));
    return nodes.map((el) => el.value);
  }

  /**
   * Complete the set keeps both Add and the sold-out email link in the DOM.
   * Pairing rows without that block still only disable Add.
   * The email link goes to the PDP for the selected variant (`?notify=1` opens Klaviyo there).
   * @param {HTMLElement} line
   * @param {HTMLElement} wrapper
   * @param {object} variant
   */
  static syncCompleteTheSetAvailability(line, wrapper, variant) {
    const addBtn = line.querySelector('.js-pairing-add-btn');
    const oosEl = line.querySelector('.cart-drawer-complete-the-set-line__oos');

    if (!oosEl) {
      if (!addBtn) return;
      if (variant.available) {
        addBtn.hidden = false;
        addBtn.removeAttribute('disabled');
        addBtn.removeAttribute('aria-disabled');
      } else {
        addBtn.setAttribute('disabled', 'disabled');
        addBtn.setAttribute('aria-disabled', 'true');
      }
      return;
    }

    if (variant.available) {
      if (addBtn) {
        addBtn.hidden = false;
        addBtn.removeAttribute('disabled');
        addBtn.removeAttribute('aria-disabled');
      }
      oosEl.hidden = true;
      return;
    }

    if (addBtn) {
      addBtn.hidden = true;
      addBtn.setAttribute('disabled', 'disabled');
      addBtn.setAttribute('aria-disabled', 'true');
    }
    oosEl.hidden = false;

    const notifyLink = oosEl.querySelector('.cart-drawer-complete-the-set-line__notify');
    const productUrl = wrapper && wrapper.dataset.productUrl;
    if (!notifyLink || !productUrl || !variant.id) return;

    try {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', String(variant.id));
      url.searchParams.set('notify', '1');
      notifyLink.href = `${url.pathname}${url.search}${url.hash}`;
    } catch (e) {
      // Keep the server-rendered product link.
    }
  }

  /**
   * @param {HTMLElement} line - .cart-drawer-pairing-line form
   */
  static syncPairingLineFromOptions(line) {
    const wrapper = line.closest('.cart-drawer-pairing-line__wrapper');
    if (!wrapper) return;

    const jsonEl = wrapper.querySelector('.cart-drawer-pairing-variants-json');
    if (!jsonEl) return;

    let variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }
    if (!variants || !variants.length) return;

    const moneyFormat = wrapper.dataset.moneyFormat || '${{amount}}';
    const selections = CartDrawer.readPairingSelections(line);
    const variant = CartDrawer.resolvePairingVariant(variants, selections);
    CartDrawer.syncOptionSelectsToVariant(line, variant);

    const idInput = line.querySelector('.js-pairing-variant-id');
    if (idInput) idInput.value = String(variant.id);

    CartDrawer.syncCompleteTheSetAvailability(line, wrapper, variant);

    const cur = line.querySelector('.js-pairing-current-price');
    const compareWrap = line.querySelector('.js-pairing-compare-wrap');
    const comparePrice = line.querySelector('.js-pairing-compare-price');
    const discountEl = line.querySelector('.js-pairing-discount');

    if (cur) {
      cur.textContent = CartDrawer.formatMoney(variant.price, moneyFormat);
      cur.setAttribute('data-cc-cents', String(variant.price));
      if (window.aesthetCurrency) window.aesthetCurrency.refreshEl(cur);
    }

    const compareAt = variant.compare_at_price;
    const price = variant.price;
    const hidePairingSalePct =
      typeof theme !== 'undefined' &&
      theme.settings &&
      theme.settings.hideCartSaleDiscountPercent === true;
    if (compareAt && compareAt > price) {
      compareWrap?.classList.remove('hidden');
      if (comparePrice) {
        comparePrice.textContent = CartDrawer.formatMoney(compareAt, moneyFormat);
        comparePrice.setAttribute('data-cc-cents', String(compareAt));
        if (window.aesthetCurrency) window.aesthetCurrency.refreshEl(comparePrice);
      }
      if (discountEl) {
        if (hidePairingSalePct) {
          discountEl.textContent = '';
        } else {
          const pct = Math.round(((compareAt - price) * 100) / compareAt);
          discountEl.textContent = `-${pct}%`;
        }
      }
    } else {
      compareWrap?.classList.add('hidden');
      if (comparePrice) {
        comparePrice.textContent = '';
        comparePrice.removeAttribute('data-cc-cents');
        comparePrice.removeAttribute('data-cc-done');
        const oldApprox = comparePrice.parentElement && comparePrice.parentElement.querySelector('.approx-price');
        if (oldApprox) oldApprox.remove();
      }
      if (discountEl) discountEl.textContent = '';
    }

    const img = line.querySelector('.js-pairing-featured-img');
    if (img && variant.featured_image && variant.featured_image.src) {
      img.src = variant.featured_image.src;
      if (img.srcset) img.removeAttribute('srcset');
    }

    const wishBtn = line.querySelector('[data-cart-swym-wishlist].cart-line-wishlist-btn');
    if (wishBtn && variant) {
      wishBtn.setAttribute('data-epi', String(variant.id));
      wishBtn.setAttribute('data-pr', String(variant.price));
      if (variant.featured_image && variant.featured_image.src) {
        wishBtn.setAttribute('data-iu', variant.featured_image.src);
      }
      if (window.theme && typeof window.theme.cartDrawerWishlistRefreshBtn === 'function') {
        window.theme.cartDrawerWishlistRefreshBtn(wishBtn);
      }
    }
  }

  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.boundPairingOptionChange = this.handlePairingOptionChange.bind(this);
    document.addEventListener('change', this.boundPairingOptionChange);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.querySelectorAll('cc-cart-pairing').forEach((el) => el.init());
      CartDrawer.syncAllCompleteTheSetLines(this);
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.querySelectorAll('cc-cart-pairing').forEach((el) => el.init());
      CartDrawer.syncAllCompleteTheSetLines(this);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    if (this.boundPairingOptionChange) {
      document.removeEventListener('change', this.boundPairingOptionChange);
    }
  }

  /**
   * @param {Event} evt - Change event.
   */
  handlePairingOptionChange(evt) {
    const select = evt.target;
    if (!select.matches || !select.matches('.cart-drawer-pairing-line__option-select')) return;
    if (!this.contains(select)) return;
    const line = select.closest('.cart-drawer-pairing-line, .cart-drawer-complete-the-set-line');
    if (!line) return;
    CartDrawer.syncPairingLineFromOptions(line);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }
}

if (!window.customElements.get('cart-drawer')) {
  window.customElements.define('cart-drawer', CartDrawer);
}
window.CartDrawerStatics = CartDrawer;

document.addEventListener('on:cart:add', (evt) => {
  if (typeof CartDrawer.hideCompleteTheSetProductsInCart !== 'function') return;
  const extraIds = [];
  if (evt.detail && evt.detail.productId) extraIds.push(evt.detail.productId);
  document.querySelectorAll('cart-drawer, cart-form').forEach((el) => {
    CartDrawer.hideCompleteTheSetProductsInCart(el, extraIds);
  });
});
